import React, { memo, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { ArrowCheckIcon, ClockIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';
import {
  LabelSize,
  LabelVariant,
} from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { ActionStepStatus, ActionStepType } from 'constants/aiChat';
import { IconSize } from 'constants/coreDesignSystem';

import { AiChatTestIds } from '../../../../AiChat.testIds';
import StepProgressIndicator from '../../../StepProgressIndicator';
import {
  isActionStepStatusActive,
  mapActionStepStatusToExecutionStepStatus,
  resolveActionStepIcon,
  resolveActionStepTypeLabel,
} from '../../ActionStep.utils';

import { STATUS_LINE_LABELS } from './StatusLine.constants';
import {
  activeLabelInnerStyles,
  activeLabelStyles,
  arrowCollapsedStyles,
  arrowExpandedStyles,
  newSinceReplanLabelStyles,
  skippedTitleStyles,
  statusLineIconStyles,
  statusLineWrapperStyles,
  titleStyles,
  typeIconBrandStyles,
  typeIconErrorStyles,
  typeIconMutedStyles,
} from './StatusLine.styles';

const StatusLine = ({
  status,
  title,
  actionStepType = null,
  toolName = null,
  isExpandable = true,
  isExpanded = false,
  isHighlightedActive = false,
  isNewSinceReplan = false,
}) => {
  const isActive = isActionStepStatusActive(status);
  const executionStatus = mapActionStepStatusToExecutionStepStatus(status);

  const TypeIcon = resolveActionStepIcon(toolName, actionStepType);
  // DEV-2913: when the row is awaiting a FE confirmation card, override
  // the type-derived label ("Running") with an explicit "Awaiting" badge
  // so the user reads it as "paused on me", not "in-flight". Variant
  // switches to ORANGE (existing in CDS palette) to differentiate from
  // RUNNING (purple) and PENDING (gray).
  // DEV-2939: the type chip now reacts to status — a completed step shows a
  // past-tense verb in GRAY instead of the present-continuous, active-colored
  // chip (e.g. purple "Running") that read as "stuck running" next to the
  // green check. See resolveActionStepTypeLabel for the full mapping.
  const { text: typeLabelText, variant: typeLabelVariant } = resolveActionStepTypeLabel(
    status,
    actionStepType,
  );
  const isModifyingRecord = actionStepType === ActionStepType.MODIFYING_RECORD;
  // DEV-2951: a skipped step never ran — mute both its tool icon and title so
  // the row reads as "didn't happen", paired with the gray "Skipped" chip.
  const isSkipped = status === ActionStepStatus.SKIPPED;
  const baseTypeIconStyles = isModifyingRecord ? typeIconErrorStyles : typeIconBrandStyles;
  const iconStyles = isSkipped ? typeIconMutedStyles : baseTypeIconStyles;
  const arrowStyles = isExpanded ? arrowExpandedStyles : arrowCollapsedStyles;
  const showTypeLabel = !_.isNil(typeLabelText);

  const arrowSx = useMemo(() => ({ ...statusLineIconStyles, ...arrowStyles }), [arrowStyles]);
  const typeIconSx = useMemo(() => ({ ...statusLineIconStyles, ...iconStyles }), [iconStyles]);

  return (
    <Box sx={statusLineWrapperStyles}>
      <Box sx={statusLineIconStyles}>
        <StepProgressIndicator isActive={isActive} status={executionStatus} />
      </Box>
      {isExpandable && (
        <Box sx={arrowSx}>
          <ArrowCheckIcon size={IconSize[16]} />
        </Box>
      )}
      {TypeIcon && (
        <Box sx={typeIconSx}>
          <TypeIcon size={IconSize[16]} />
        </Box>
      )}
      {showTypeLabel && (
        <LabelCDS withBorder size={LabelSize.S} title={typeLabelText} variant={typeLabelVariant} />
      )}
      <Typography
        data-testid={AiChatTestIds.ACTION_STEP_TITLE}
        sx={isSkipped ? skippedTitleStyles : titleStyles}
        variant="medium14"
      >
        {title}
      </Typography>
      {isHighlightedActive && (
        <Box sx={activeLabelStyles}>
          <LabelCDS
            withBorder
            size={LabelSize.S}
            title={
              <Box sx={activeLabelInnerStyles}>
                <ClockIcon size={IconSize[12]} />
                {STATUS_LINE_LABELS.ACTIVE}
              </Box>
            }
            variant={LabelVariant.BLUE}
          />
        </Box>
      )}
      {isNewSinceReplan && (
        <Box sx={newSinceReplanLabelStyles}>
          <LabelCDS
            withBorder
            size={LabelSize.S}
            title={
              <Typography variant="regular12">{STATUS_LINE_LABELS.NEW_SINCE_REPLAN}</Typography>
            }
            variant={LabelVariant.BLUE}
          />
        </Box>
      )}
    </Box>
  );
};

StatusLine.propTypes = {
  status: PropTypes.oneOf(Object.values(ActionStepStatus)).isRequired,
  title: PropTypes.string.isRequired,
  actionStepType: PropTypes.oneOf(Object.values(ActionStepType)),
  toolName: PropTypes.string,
  isExpandable: PropTypes.bool,
  isExpanded: PropTypes.bool,
  isHighlightedActive: PropTypes.bool,
  isNewSinceReplan: PropTypes.bool,
};

export default memo(StatusLine);
